import React, { useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as StarIcon } from '@pkg/assets/icons/v2/star.svg';
import YatDashboardTopBarUserNavigation from '@pkg/components/Dashboard/TopBar/UserNavigation/YatDashboardTopBarUserNavigation';
import { GlobalSearch } from '@pkg/components/GlobalSearch';
import { ImageIcon } from '@pkg/components/Sections/Profile/ImageIcon';
import { getRemainingInvites } from '@pkg/components/deprecated';
import { useAppSelector, useBreakpoint } from '@pkg/hooks';
import { useGetInviteData } from '@pkg/hooks/deprecated';

import { Popover } from '@app/pages/Prototype/components/Profile/components/Popover';
import { PathParams } from '@app/routes/types';

import { ReactComponent as EditIcon } from '../../assets/icons/v2/edit.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/v2/plus.svg';
import WavingHand from '../../assets/images/Waving_Hand.png';
import { Button, ButtonIcon } from '../Sections/Profile/Buttons';

import { MainNavigationItem } from './MainNavigationItem';
import { SparklesIcon, UserIcon, WalletIcon } from './icons';
import { ActionItem, type MainNavigationProps, MenuItem } from './types';

import {
    ActionButtonWrapper,
    ActionsWrapper,
    ButtonsWrapper,
    EditButtonWrapper,
    LiquidIdWrapper,
    MainNavigationContainer,
    MainNavigationGrid,
    NotificationDot,
    SearchWrapper,
} from './styles';

export const LOCAL_STORAGE_EDIT_PROFILE_PROMPT_SEEN = 'editProfilePromptSeen';
export const LOCAL_STORAGE_ADD_WALLETS_PROMPT_SEEN = 'manageWalletsPromptSeen';

export const MainNavigation = ({
    activeItem,
    onSelectMenuItem,
    onSelectActionItem,
    createYatsButtonText,
    notifyDotProfile,
    absolutePositionedLiquidId,
    disableLiquidId,
    ...others
}: MainNavigationProps) => {
    const { t } = useTranslation(['yat_page', 'dashboard', 'translation', 'common', 'words']);
    const { [PathParams.Yat]: yat } = useParams();
    const { isWithinBreakpoint: isTabletOrSmaller } = useBreakpoint('tabletSmall');

    const isEditingProfile = useMatch('/profile/edit');
    const isYativerse = useMatch('/yativerse');
    const isCollectionsPage = useMatch('/collection/*');

    const primaryYat = useAppSelector((state) => state.user?.primaryYat);
    const showInviteToYat = useAppSelector((state) => state.featureFlags.features.showInviteToYat);
    const showCollectionPageWip = useAppSelector((state) => state.featureFlags.features.showCollectionPageWip);
    const isCurrentUsersProfile = primaryYat == yat;
    const navigate = useNavigate();

    const { data: inviteData } = useGetInviteData();

    const invitesRemaining = getRemainingInvites(inviteData);

    const mouse = useRef({ x: 0, y: 0 });

    const onLiquidIDMouseDown = (event) => {
        mouse.current.x = event.clientX;
        mouse.current.y = event.clientY;
    };

    const onLiquidIDMouseUp = (event) => {
        const x = event.clientX - mouse.current.x;
        const y = event.clientY - mouse.current.y;
        if (Math.sqrt(x * x + y * y) < 10) onLiquidIDClick();
    };

    const onLiquidIDClick = () => {
        if (primaryYat) navigate(`/${primaryYat}?lid=1`, { state: { enableGoBack: true } });
        else onSelectMenuItem(MenuItem.Profile);
    };

    const canRenderCube =
        !isEditingProfile && !yat && !disableLiquidId && !(isCollectionsPage && !showCollectionPageWip);

    const cubeMarkup2 = canRenderCube && (
        <div onMouseDown={onLiquidIDMouseDown} onMouseUp={onLiquidIDMouseUp}>
            <LiquidIdWrapper
                standalone
                $absolutePositioned={absolutePositionedLiquidId}
                $isYativerse={isYativerse !== null}
            />
        </div>
    );

    const inviteButtonMarkup = showInviteToYat && (
        <Button
            variant="highlight"
            size="small"
            fluid
            startIcon={<ImageIcon src={WavingHand} altText={t('common:emoji', { emoji: 'Waving Hand' })} />}
            indicatorValue={invitesRemaining}
            onClick={() => {
                onSelectActionItem(ActionItem.InviteToYat, invitesRemaining);
            }}
        >
            {isTabletOrSmaller ? t('words:invite') : t('dashboard:invite_to_yat', { yat: t('translation:yat') })}
        </Button>
    );

    const createButtonMarkup = isTabletOrSmaller ? (
        <ButtonIcon onClick={() => onSelectActionItem(ActionItem.CreateYat)} color="transparent" size="small">
            <PlusIcon />
        </ButtonIcon>
    ) : (
        <Button
            onClick={() => onSelectActionItem(ActionItem.CreateYat)}
            variant="solid"
            color="translucent"
            size="small"
            startIcon={<PlusIcon />}
            fluid
        >
            {createYatsButtonText}
        </Button>
    );

    const searchMarkup = !isCurrentUsersProfile && (
        <SearchWrapper $hasLiquidId={!disableLiquidId}>
            <GlobalSearch />
        </SearchWrapper>
    );

    const editButtonMarkup = isCurrentUsersProfile && primaryYat && (
        <EditButtonWrapper>
            <Button
                variant="highlight"
                size="small"
                startIcon={<EditIcon />}
                to="/profile/edit"
                style={{ minWidth: '141px' }}
            >
                {t('edit_profile')}
            </Button>
            <Popover
                title={t('edit_profile_popover_title')}
                text={t('edit_profile_popover_text')}
                localStorageKey={LOCAL_STORAGE_EDIT_PROFILE_PROMPT_SEEN}
                positionArrow="top-left"
            />
        </EditButtonWrapper>
    );

    return (
        <MainNavigationContainer {...others}>
            {cubeMarkup2}

            <MainNavigationGrid>
                {searchMarkup}
                {editButtonMarkup}

                <ButtonsWrapper>
                    <MainNavigationItem
                        item={MenuItem.Profile}
                        isActive={activeItem === MenuItem.Profile && isCurrentUsersProfile}
                        label="Profile"
                        onClick={() => onSelectMenuItem(MenuItem.Profile)}
                    >
                        <UserIcon isActive={activeItem === MenuItem.Profile} />
                        {notifyDotProfile && !isCurrentUsersProfile && <NotificationDot />}
                    </MainNavigationItem>

                    <MainNavigationItem
                        item={MenuItem.Yativerse}
                        isActive={activeItem === MenuItem.Yativerse}
                        label="Yativerse"
                        onClick={() => onSelectMenuItem(MenuItem.Yativerse)}
                    >
                        <SparklesIcon isActive={activeItem === MenuItem.Yativerse} />
                    </MainNavigationItem>
                </ButtonsWrapper>

                <ActionsWrapper>
                    <ActionButtonWrapper>{inviteButtonMarkup}</ActionButtonWrapper>
                    <ActionButtonWrapper>{createButtonMarkup}</ActionButtonWrapper>
                    <ActionButtonWrapper>
                        <ButtonIcon to="/wishlists?goBack=1" color="transparent" size="small">
                            <StarIcon />
                        </ButtonIcon>
                    </ActionButtonWrapper>
                    <ActionButtonWrapper>
                        <ButtonIcon
                            onClick={() => onSelectActionItem(ActionItem.Wallet)}
                            color="transparent"
                            size="small"
                        >
                            <WalletIcon />
                        </ButtonIcon>
                    </ActionButtonWrapper>
                    <ActionButtonWrapper>
                        <YatDashboardTopBarUserNavigation variant="v2" isCurrentUsersProfile={isCurrentUsersProfile} />
                    </ActionButtonWrapper>
                </ActionsWrapper>
            </MainNavigationGrid>
        </MainNavigationContainer>
    );
};
